<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { UseDraggable } from '@vueuse/components'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import Dialog from 'primevue/dialog'
import {
  ArrowRightLeft,
  Maximize2,
  Mic,
  MicOff,
  Pause,
  Phone,
  PhoneOff,
  Play,
} from 'lucide-vue-next'

import { WebPhone } from '@tmsoft/webphone'
import type {
  WebPhoneCallContext,
  WebPhoneCallStateEvent,
  WebPhoneMediaEvent,
} from '@tmsoft/webphone'

import AppButton from '@/components/atoms/buttons/AppButton.vue'
import { useAuthStore } from '@/stores/useAuthStore'
import { useCallAiStore } from '@/stores/useCallAiStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { usePauseStatusStore } from '@/stores/usePauseStatusStore'
import { useCampaignRegistrationStore } from '@/stores/useCampaignRegistrationStore'
import { useConnectivityStore } from '@/stores/useConnectivityStore'
import { useCampaignRegistrationSync } from '@/composables/useCampaignRegistrationSync'
import { useNotifications } from '@/composables/useNotifications'
import { useWebphonePermissions } from '@/composables/useWebphonePermissions'
import { useContactService } from '@/services/contact/useContactService'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { useCallService } from '@/services/call/useCallService'
import { getWebSocketUrl } from '@/helpers/api-url.helper'
import { isAxiosError } from 'axios'

const props = withDefaults(
  defineProps<{
    sidebarExpanded?: boolean
  }>(),
  {
    sidebarExpanded: false,
  },
)

const SIDEBAR_COLLAPSED_WIDTH = 64
const SIDEBAR_EXPANDED_WIDTH = 200
const WEBPHONE_SIDEBAR_OFFSET = 22
const WEBPHONE_TOP_OFFSET = 100
const WEBPHONE_CAMPAIGN_ALERT_OFFSET = 36
const LARGE_CALL_VIEW_MEDIA_QUERY = '(min-width: 1536px)' // sm: 640px, md: 768px, lg: 1024px, xl: 1280px, 2xl: 1536px
const SMALL_WEBPHONE_MEDIA_QUERY = '(max-width: 1023px)'
const CALL_ENDED_EVENT = 'calloperator:call-ended'
const MONITOR_HEARTBEAT_MS = 25000
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1'])
const FORCE_MASK_NUMBER_HOSTNAME = 'ljgh.anw.cloud'
const extensionDominio = ref(
  typeof window === 'undefined'
    ? ''
    : LOCAL_HOSTNAMES.has(window.location.hostname)
      ? 'tmsoft.anw.cloud'
      : window.location.hostname,
)
const extensionNumber = computed(() => authStore.currentUser?.extension || '')
const extensionPassword = computed(() => authStore.currentUser?.password_extension || '')

const authStore = useAuthStore()
const callAiStore = useCallAiStore()
const workspace = useWorkspaceStore()
const pauseStatusStore = usePauseStatusStore()
const campaignRegistrationStore = useCampaignRegistrationStore()
const { refreshChatRegistration } = useCampaignRegistrationSync()
const connectivityStore = useConnectivityStore()
const shouldMaskWebphoneNumber = computed(
  () => extensionDominio.value === FORCE_MASK_NUMBER_HOSTNAME || workspace.callMaskNumber,
)
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const toast = useToast()
const { checkMicrophonePermission, ensureMicrophoneAccess, microphonePermission } =
  useWebphonePermissions()
const { createContact, getContactById, searchContact } = useContactService()
const { getCampaignById, getCampaignByQueueId } = useCampaignService()
const { registerManualCallIntent } = useCallService()
const notifications = useNotifications()
// El aviso vive en el store porque llega por dos caminos —este socket y el de
// chat (AGENT_EJECTED_FROM_CAMPAIGNS)— y ninguno cubre a todos los agentes: el
// de aquí va filtrado por extensión, que el agente de solo chat no tiene.
const showEjectedDialog = computed({
  get: () => campaignRegistrationStore.isEjectionNoticeVisible,
  set: (value: boolean) => {
    if (value) campaignRegistrationStore.showEjectionNotice()
    else campaignRegistrationStore.dismissEjectionNotice()
  },
})
const directCallReturnRoute = ref('/')
const isDirectCallSelectionActive = ref(false)
const markerDialContext = ref<{ phone: string; campaignId: string } | null>(null)
const isDirectWebphoneDialPending = ref(false)
const isOutboundDialStarting = ref(false)
const outboundDisplayNumber = ref('')
let webphoneEventRoot: HTMLElement | null = null

const homeRouteNames = new Set(['home', 'campaign-detail', 'campaign-preview', 'direct-call'])
const fullWebphoneRouteNames = new Set(['home', 'campaign-preview'])
const callViewRouteNames = new Set(['campaign-detail', 'direct-call'])
const showWebphone = computed(() => authStore.showWebphone)
const isExtensionActiveElsewhere = computed(() => authStore.extensionIsActive)
const hasVoiceCampaign = computed(() => workspace.hasVoiceCampaign)
const isRegisteredInCampaign = computed(() => campaignRegistrationStore.isRegisteredInCampaign)
const hasAssignedExtension = computed(() => Boolean(authStore.userExtension))
const isMicrophoneReady = computed(() => microphonePermission.value === 'granted')
const isWebphoneDisconnected = computed(() => connectivityStore.isAnyServiceDisconnected)
const isOutboundCallDisabled = computed(
  () =>
    isExtensionActiveElsewhere.value ||
    !hasAssignedExtension.value ||
    !authStore.webphoneAvailable ||
    isWebphoneDisconnected.value ||
    !isMicrophoneReady.value,
)
const outboundCallDisabledMessage = computed(() => {
  if (isExtensionActiveElsewhere.value) {
    return t('header.webphone_extension_active')
  }

  if (!hasAssignedExtension.value || !authStore.webphoneAvailable) {
    return t('header.webphone_no_extension_assigned')
  }

  if (isWebphoneDisconnected.value) {
    return t('header.webphone_no_connection')
  }

  return t('call.microphone_access_required')
})
const activeWebphoneStatuses = new Set(['incoming', 'calling', 'connected', 'held'])
const shouldPreferFullWebphoneRoute = computed(() =>
  fullWebphoneRouteNames.has(String(route.name ?? '')),
)
const hasActiveWebphoneCall = computed(() =>
  activeWebphoneStatuses.has(workspace.webphoneCallStatus),
)
const isCallViewRoute = computed(() => callViewRouteNames.has(String(route.name ?? '')))
const isLargeCallViewViewport = ref(false)
const isSmallWebphoneViewport = ref(false)
let largeCallViewMediaQuery: MediaQueryList | null = null
let smallWebphoneMediaQuery: MediaQueryList | null = null
const shouldKeepFullWebphoneOnLargeCallView = computed(
  () => hasActiveWebphoneCall.value && isCallViewRoute.value && isLargeCallViewViewport.value,
)
const shouldKeepFullWebphoneWithoutVoiceCampaign = computed(
  () => hasActiveWebphoneCall.value && !hasVoiceCampaign.value,
)
const shouldDisplayWebphone = computed(
  () =>
    !isExtensionActiveElsewhere.value &&
    (hasActiveWebphoneCall.value || isOutboundDialStarting.value || showWebphone.value),
)
const shouldShowCompactWebphone = computed(
  () =>
    hasActiveWebphoneCall.value &&
    !shouldKeepFullWebphoneWithoutVoiceCampaign.value &&
    !shouldPreferFullWebphoneRoute.value &&
    !shouldKeepFullWebphoneOnLargeCallView.value &&
    !workspace.webphonePipExpanded,
)
const shouldShowFullWebphone = computed(
  () => shouldDisplayWebphone.value && !shouldShowCompactWebphone.value,
)
const isCompactMuted = ref(false)
const isCompactHeld = ref(false)
const hasAutoOpenedInHome = ref(false)
const containerRef = ref<HTMLElement | null>(null)
const manualDialTarget = ref('')
const manualDialToken = ref(0)
const lastDialAttemptKey = ref('')
const lastCallNavigationKey = ref('')
const lastIncomingNotificationKey = ref('')
const isCallNavigationInProgress = ref(false)
const lastShiftActivityRefreshCallKey = ref('')
const monitorSocket = ref<WebSocket | null>(null)
const lastIncomingInviteKey = ref('')
const pendingIncomingQueueCall = ref<{
  callId: string
  campaignId: string
  returnRoute: string
} | null>(null)
const answeredIncomingCallKey = ref('')
const lastAnsweredIncomingNavigationKey = ref('')
const isMonitorSipConnected = ref(false)
const isWebphoneSipConnected = ref(false)
let monitorReconnectTimer: number | null = null
let monitorHeartbeatTimer: number | null = null
let isMonitorUnmounted = false
let isIntentionalMonitorClose = false
let isWebphoneLogoutInProgress = false
const webphoneRef = ref<InstanceType<typeof WebPhone> | null>(null)
let webphoneObserver: MutationObserver | null = null
let webphoneObserverRetryTimer: number | null = null
let browserExitListenersAttached = false
let unloadHangupTriggered = false
const ACTIVE_CALL_UNLOAD_WARNING =
  'Tienes una llamada activa. ¿Estás seguro de que deseas recargar o cerrar la página? Las llamadas activas se colgarán.'
const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))
const showWebphoneDebug = ref(true)
const webphoneDebug = reactive({
  state: 'idle',
  channelId: null as number | null,
  sipCallId: '',
  direction: '',
  remoteNumber: '',
  queue: '',
  localTracks: [] as string[],
  remoteTracks: [] as string[],
  updatedAt: '',
})

const updateGlobalSipConnectivity = () => {
  connectivityStore.setSipConnected(isMonitorSipConnected.value && isWebphoneSipConnected.value)
}

const setMonitorSipConnected = (isConnected: boolean) => {
  isMonitorSipConnected.value = isConnected
  updateGlobalSipConnectivity()
}

const setWebphoneSipConnected = (isConnected: boolean) => {
  isWebphoneSipConnected.value = isConnected
  updateGlobalSipConnectivity()
}

const updateDebugContext = (context: WebPhoneCallContext) => {
  webphoneDebug.channelId = context.channelId
  webphoneDebug.sipCallId = context.sipCallId
  webphoneDebug.direction = context.direction
  webphoneDebug.remoteNumber = context.remoteNumber
  // La cola viene en los headers X-Nebula-Queue del propio INVITE (ver el
  // pre-dial handler de Queue() en Asterisk). Solo llega en entrantes que
  // pasaron por cola: una llamada directa a la extensión no la trae.
  webphoneDebug.queue = context.queue?.name || context.queue?.id || ''
  webphoneDebug.updatedAt = new Date().toLocaleTimeString()
}

const describeAudioTracks = (stream: MediaStream) =>
  stream
    .getAudioTracks()
    .map((track) => `${track.id} - ${track.readyState} - enabled=${track.enabled}`)

/*
const debugAudioChunks = computed(() => callAiStore.debugAudioChunks)

const formatDebugChunkTime = (value: number) =>
  new Date(value).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

const formatDebugChunkDuration = (value: number) => `${(value / 1000).toFixed(1)}s`

const formatDebugChunkSize = (value: number) => `${Math.max(value / 1024, 0.1).toFixed(1)} KB`

const downloadDebugChunk = (chunk: DebugAudioChunk) => {
  const url = URL.createObjectURL(chunk.wavBlob)
  const link = document.createElement('a')
  link.href = url
  link.download = `webphone-${chunk.party}-${chunk.startedAt}.wav`
  link.click()
  URL.revokeObjectURL(url)
}
*/

const handleCallContext = (context: WebPhoneCallContext) => {
  setWebphoneSipConnected(true)
  updateDebugContext(context)
  if (context.direction === 'incoming') {
    outboundDisplayNumber.value = ''
    // Evita reutilizar el enmascaramiento de la llamada anterior mientras se
    // resuelve la campaña de la nueva llamada a partir de la cola del INVITE.
    workspace.setCallMaskNumber(false)
  } else {
    outboundDisplayNumber.value = normalizePhone(context.remoteNumber || outboundDisplayNumber.value)
  }
  void loadIncomingCallContext(context)
  console.info('[WebPhone debug] call-context', context)
}

const closeCampaignDetailAfterCallEnd = async () => {
  if (route.name !== 'campaign-detail' && route.name !== 'direct-call') return
  if (workspace.hasPendingFormChanges) return

  const returnRoute = workspace.callReturnRoute || '/'

  if (route.name === 'direct-call') {
    window.dispatchEvent(new CustomEvent('direct-call-view-closed'))
  }

  workspace.clearCallNavigationContext()

  if (route.fullPath !== returnRoute) {
    await router.push(returnRoute)
  }
}

const shouldNavigateToCallView = (event: WebPhoneCallStateEvent) =>
  ['incoming', 'dialing', 'ringing', 'connected', 'held'].includes(event.state)

const callSessionKey = (event: WebPhoneCallStateEvent | WebPhoneMediaEvent) =>
  String(event.sipCallId || event.channelId || event.remoteNumber || 'unknown-call')

const incomingCallSessionKey = (context: WebPhoneCallContext) =>
  String(context.sipCallId || context.channelId || context.remoteNumber || 'unknown-call')

const isIncomingCallState = (event: WebPhoneCallStateEvent, eventState: string) =>
  eventState === 'incoming' ||
  (event.direction === 'incoming' &&
    ['dialing', 'ringing', 'connected', 'held'].includes(eventState))

const notifyIncomingCall = (payload: { phoneNumber?: string; callId: string }) => {
  if (lastIncomingNotificationKey.value === payload.callId) return

  lastIncomingNotificationKey.value = payload.callId
  void notifications.notifyIncomingCall(payload)
}

const notifyCallEnded = (event: WebPhoneCallStateEvent) => {
  const callId = callSessionKey(event)
  if (lastShiftActivityRefreshCallKey.value === callId) return

  lastShiftActivityRefreshCallKey.value = callId
  window.dispatchEvent(
    new CustomEvent(CALL_ENDED_EVENT, {
      detail: {
        callId,
        state: event.state,
        direction: event.direction,
      },
    }),
  )
}

const resetWebphoneCallState = () => {
  workspace.setWebphoneCallState({ status: 'idle', duration: '00:00' })
  isCompactMuted.value = false
  isCompactHeld.value = false
}

const closeNativePictureInPicture = () => {
  try {
    window.documentPictureInPicture?.window?.close()
  } catch {
    // Browser already closed it or does not expose the window anymore.
  }
}

const restoreWebphoneFromBrowserTab = () => {
  if (document.visibilityState !== 'visible' || !hasActiveWebphoneCall.value) return

  closeNativePictureInPicture()
  authStore.setShowWebphone(true)
  workspace.setWebphonePipExpanded(true)
}

const openDirectCallView = (phone: string) => {
  const hasCampaignContext = Boolean(workspace.callCampaignId)
  if (isDirectCallSelectionActive.value || route.name === 'direct-call' || hasCampaignContext) {
    return
  }

  directCallReturnRoute.value = route.fullPath
  isDirectCallSelectionActive.value = true
  workspace.setContact(null)
  workspace.setContactPhone(phone)
  workspace.startDirectCallNavigation(directCallReturnRoute.value)
  window.setTimeout(() => {
    void router.push({ name: 'direct-call' })
  }, 0)
}

const scheduleMonitorReconnect = () => {
  if (isMonitorUnmounted || monitorReconnectTimer !== null) return

  monitorReconnectTimer = window.setTimeout(() => {
    monitorReconnectTimer = null
    connectMonitor()
  }, 3000)
}

const stopMonitorHeartbeat = () => {
  if (monitorHeartbeatTimer === null) return

  window.clearInterval(monitorHeartbeatTimer)
  monitorHeartbeatTimer = null
}

// Este websocket no tenía keepalive en ninguna dirección: el backend solo
// contesta PING (nunca lo inicia) y su único tráfico propio es el
// QueueMemberGrouped del poll de colas, que no existe para un agente que no es
// miembro de ninguna. Sin bytes de por medio, un intermediario ocioso corta la
// conexión y el operador solo se entera al volver a la pestaña.
const startMonitorHeartbeat = () => {
  stopMonitorHeartbeat()

  monitorHeartbeatTimer = window.setInterval(() => {
    const socket = monitorSocket.value
    if (socket?.readyState !== WebSocket.OPEN) return

    try {
      socket.send(JSON.stringify({ event: 'PING' }))
    } catch {
      // El onclose del socket se encarga de la reconexión.
    }
  }, MONITOR_HEARTBEAT_MS)
}

// El reintento de scheduleMonitorReconnect es un timer de 3 s, y en segundo
// plano el navegador lo estrangula (o lo congela), así que un socket muerto
// podía quedarse así hasta bastante después de volver. Al recuperar la pestaña
// se mira el estado real en vez de esperar al timer.
const revalidateMonitorConnection = () => {
  if (isMonitorUnmounted || !authStore.isAuthenticated) return
  if (document.visibilityState !== 'visible') return

  // CONNECTING también se respeta: ese intento ya está en curso y, con la pestaña
  // otra vez visible, su onclose cae en un scheduleMonitorReconnect sin estrangular.
  const socket = monitorSocket.value
  if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) return

  if (socket) {
    // Sin handlers, para que el onclose tardío del socket viejo no marque una
    // caída ni programe un reintento en paralelo con este.
    socket.onopen = null
    socket.onmessage = null
    socket.onclose = null
    socket.onerror = null
    socket.close()
    monitorSocket.value = null
  }

  if (monitorReconnectTimer !== null) {
    window.clearTimeout(monitorReconnectTimer)
    monitorReconnectTimer = null
  }

  connectMonitor()
}

const connectMonitor = () => {
  if (isExtensionActiveElsewhere.value) {
    setMonitorSipConnected(true)
    return
  }

  const token = authStore.accessToken
  const extension = extensionNumber.value
  if (!token || !extension || monitorSocket.value || isMonitorUnmounted) {
    // Durante login las credenciales/extensión pueden llegar unos ms después
    // del montaje. Ese estado es carga, no una desconexión operativa.
    if (!token || !extension || !authStore.isAuthenticated) setMonitorSipConnected(true)
    return
  }

  const query = new URLSearchParams({
    channel: 'calloperator',
    extension,
    token,
  })
  const socket = new WebSocket(`${getWebSocketUrl('api/monitor/ws')}?${query}`)
  monitorSocket.value = socket
  isIntentionalMonitorClose = false

  socket.onopen = () => {
    setMonitorSipConnected(true)
    startMonitorHeartbeat()
    // Arranca el reloj del registro de voz: si no llega ningún QueueMemberGrouped
    // propio, el estado pasa de "desconocido" a "no registrado" por sí solo.
    campaignRegistrationStore.beginVoiceHeartbeatWatch()
  }

  socket.onmessage = (message) => {
    // Recibir eventos del monitor confirma que telefonía/SIP sigue disponible
    // para este operador sin depender de recargar la pantalla.
    setMonitorSipConnected(true)
    try {
      const event = JSON.parse(message.data) as {
        event_name?: string
        event_data?: {
          Extension?: string
          Interface?: string
          Paused?: boolean | string
          PausedReason?: string
          LastPause?: string
          Reason?: string
        }
      }

      // Snapshot periódico (poll de 3s de queue_monitor_loop) con el estado real
      // del agente en Asterisk. Es la ÚNICA fuente de verdad del registro en
      // campaña de voz: Asterisk solo reporta a los agentes que son miembros de
      // alguna cola, así que mientras el evento llega el agente está registrado
      // y cuando deja de llegar (expulsión, salida de campaña, AMI caído) el
      // registro expira solo en el store.
      //
      // El estado de pausa lo reconcilia el store con el motivo que reporta
      // Asterisk: si ya coincide con lo que sabíamos no toca nada, para no perder
      // el pause_id de catálogo de una pausa propia con cada poll.
      if (event.event_name === 'QueueMemberGrouped') {
        if (event.event_data?.Extension !== extensionNumber.value) return

        campaignRegistrationStore.markVoiceHeartbeat()

        const isPausedNow = String(event.event_data?.Paused) === '1'
        if (isPausedNow) {
          const lastPauseSeconds = Number(event.event_data?.LastPause)
          const startedAt =
            Number.isFinite(lastPauseSeconds) && lastPauseSeconds > 0
              ? lastPauseSeconds * 1000
              : undefined

          // El motivo que devuelve Asterisk es el que mandó quien pausó, así
          // que el store puede decidir con él si la pausa es del supervisor o
          // del propio agente.
          pauseStatusStore.applyPauseFromAsterisk(
            event.event_data?.PausedReason,
            startedAt,
          )
        } else if (pauseStatusStore.isPaused) {
          pauseStatusStore.clearPause()
        }
        return
      }

      // Pausa/reanudación/expulsión forzadas por un supervisor desde el monitor —
      // mismo nombre que el evento real de Asterisk, ya llegan filtradas por la
      // propia extensión (kafka_monitor_service, vía el campo Interface).
      if (event.event_name === 'QueueMemberPause') {
        if (event.event_data?.Paused) {
          pauseStatusStore.applyPauseFromAsterisk(event.event_data.Reason)
        } else {
          pauseStatusStore.clearPause()
        }
        return
      }

      // Expulsión por un supervisor desde el monitor — evento propio, distinto del
      // "QueueMemberRemoved" crudo que Asterisk dispara para cualquier otra baja de
      // cola (antes compartían nombre y se distinguían con un flag FromMonitor, lo
      // que generaba confusión al llegar dos eventos iguales por una sola acción).
      if (event.event_name === 'QueueMemberEjected') {
        campaignRegistrationStore.showEjectionNotice()
        pauseStatusStore.clearPause()

        // El backend cierra el campaign_login ENTERO antes de publicar esto, así
        // que la expulsión saca al agente de todas sus campañas, chat incluido.
        // La voz la confirma el heartbeat (si sigue siendo miembro de otras colas
        // el evento sigue llegando y el registro se sostiene); el chat hay que
        // volver a preguntarlo o se quedaría en el último valor conocido, que
        // dejaría al agente viendo "reintentar telefonía" en vez de "iniciar
        // campaña" — y ese reintento reabriría un registro solo de voz.
        campaignRegistrationStore.expectVoiceUnregistration()
        void refreshChatRegistration()
      }
    } catch {
      // Ignore non-JSON monitor messages such as heartbeat responses.
    }
  }

  socket.onclose = () => {
    stopMonitorHeartbeat()
    if (monitorSocket.value === socket) monitorSocket.value = null
    if (isIntentionalMonitorClose || isMonitorUnmounted || !authStore.isAuthenticated) {
      isIntentionalMonitorClose = false
      setMonitorSipConnected(true)
      return
    }

    setMonitorSipConnected(false)
    scheduleMonitorReconnect()
  }

  socket.onerror = () => {
    setMonitorSipConnected(false)
    socket.close()
  }
}

const handleCallState = (event: WebPhoneCallStateEvent) => {
  setWebphoneSipConnected(event.state !== 'failed')
  updateDebugContext(event)
  const eventState = String(event.state)
  webphoneDebug.state = eventState
  const isIncomingCall = isIncomingCallState(event, eventState)
  const status =
    isIncomingCall && (eventState === 'incoming' || eventState === 'ringing')
      ? 'incoming'
      : eventState === 'connected'
        ? 'connected'
        : eventState === 'held'
          ? 'held'
          : eventState === 'ended' || eventState === 'failed'
            ? 'idle'
            : eventState === 'dialing' || eventState === 'ringing'
              ? 'calling'
              : null

  if (status) {
    workspace.setWebphoneCallState({
      status,
      duration: status === 'idle' ? '00:00' : workspace.webphoneCallDuration,
    })

    if (status === 'idle') {
      isOutboundDialStarting.value = false
      isCompactMuted.value = false
      isCompactHeld.value = false
    } else if (status === 'held') {
      isCompactHeld.value = true
    }
  }

  if (event.direction === 'outgoing' && status && status !== 'idle') {
    isOutboundDialStarting.value = false
    outboundDisplayNumber.value = normalizePhone(
      event.remoteNumber || outboundDisplayNumber.value || manualDialTarget.value,
    )
  }

  if (shouldNavigateToCallView(event)) {
    authStore.setShowWebphone(true)
  }

  if (status === 'incoming' || (isIncomingCall && eventState === 'connected')) {
    notifyIncomingCall({
      phoneNumber: event.remoteNumber,
      callId: callSessionKey(event),
    })
  }

  if (isIncomingCall && eventState === 'connected') {
    const callId = callSessionKey(event)
    answeredIncomingCallKey.value = callId
    void navigateToAnsweredIncomingCampaign(callId)
  }

  if (event.state === 'dialing' || event.state === 'ringing' || event.state === 'connected') {
    lastShiftActivityRefreshCallKey.value = ''
    callAiStore.beginCallSession(callSessionKey(event))
  }
  if (
    event.direction === 'outgoing' &&
    (event.state === 'dialing' || event.state === 'ringing' || event.state === 'connected')
  ) {
    const hasCampaignContext = Boolean(workspace.callCampaignId)
    if (!markerDialContext.value && !hasCampaignContext) {
      openDirectCallView(
        normalizePhone(event.remoteNumber || workspace.contactPhone || manualDialTarget.value),
      )
    }
    void ensureContactRequestViewForCall(event)
  }
  if (event.state === 'ended' || event.state === 'failed') {
    notifyCallEnded(event)
    markerDialContext.value = null
    isDirectWebphoneDialPending.value = false
    isOutboundDialStarting.value = false
    outboundDisplayNumber.value = ''
    lastIncomingInviteKey.value = ''
    lastIncomingNotificationKey.value = ''
    pendingIncomingQueueCall.value = null
    answeredIncomingCallKey.value = ''
    lastAnsweredIncomingNavigationKey.value = ''
    if (callAiStore.isAutoTranscribing) {
      void callAiStore.stopAutoTranscription()
    }
    callAiStore.endCallSession()
    lastCallNavigationKey.value = ''
    void closeCampaignDetailAfterCallEnd()
  }
  console.info('[WebPhone debug] call-state', event)
}

const handleMediaReady = (event: WebPhoneMediaEvent) => {
  setWebphoneSipConnected(true)
  updateDebugContext(event)
  callAiStore.beginCallSession(callSessionKey(event))
  webphoneDebug.localTracks = describeAudioTracks(event.localStream)
  webphoneDebug.remoteTracks = describeAudioTracks(event.remoteStream)
  window.__callOperatorWebphoneMedia = {
    localStream: event.localStream,
    remoteStream: event.remoteStream,
    updatedAt: Date.now(),
  }
  window.dispatchEvent(
    new CustomEvent('calloperator:webphone-media-ready', {
      detail: window.__callOperatorWebphoneMedia,
    }),
  )
  callAiStore.setMediaStreams({
    localStream: event.localStream,
    remoteStream: event.remoteStream,
  })
  console.info('[WebPhone debug] media-ready', event)
}

const syncLargeCallViewViewport = (event: MediaQueryList | MediaQueryListEvent) => {
  isLargeCallViewViewport.value = event.matches
}

const syncSmallWebphoneViewport = (event: MediaQueryList | MediaQueryListEvent) => {
  isSmallWebphoneViewport.value = event.matches
}

const connectLargeCallViewViewportListener = () => {
  if (typeof window === 'undefined' || largeCallViewMediaQuery) return

  largeCallViewMediaQuery = window.matchMedia(LARGE_CALL_VIEW_MEDIA_QUERY)
  syncLargeCallViewViewport(largeCallViewMediaQuery)
  largeCallViewMediaQuery.addEventListener('change', syncLargeCallViewViewport)
}

const connectSmallWebphoneViewportListener = () => {
  if (typeof window === 'undefined' || smallWebphoneMediaQuery) return

  smallWebphoneMediaQuery = window.matchMedia(SMALL_WEBPHONE_MEDIA_QUERY)
  syncSmallWebphoneViewport(smallWebphoneMediaQuery)
  smallWebphoneMediaQuery.addEventListener('change', syncSmallWebphoneViewport)
}

const disconnectLargeCallViewViewportListener = () => {
  largeCallViewMediaQuery?.removeEventListener('change', syncLargeCallViewViewport)
  largeCallViewMediaQuery = null
}

const disconnectSmallWebphoneViewportListener = () => {
  smallWebphoneMediaQuery?.removeEventListener('change', syncSmallWebphoneViewport)
  smallWebphoneMediaQuery = null
}

onMounted(() => {
  isMonitorUnmounted = false
  connectLargeCallViewViewportListener()
  connectSmallWebphoneViewportListener()
  notifications.initializeAudio()
  void checkMicrophonePermission().finally(syncOutboundCallAvailability)
  window.addEventListener('webphone-manual-call', handleManualCallEvent as EventListener)
  window.addEventListener(
    'direct-call-campaign-selected',
    handleDirectCallCampaignSelected as EventListener,
  )
  window.addEventListener('direct-call-view-closed', handleDirectCallViewClosed)
  window.addEventListener('calloperator:webphone-logout', handleWebphoneLogout)
  document.addEventListener('visibilitychange', restoreWebphoneFromBrowserTab)
  window.addEventListener('focus', restoreWebphoneFromBrowserTab)
  document.addEventListener('visibilitychange', revalidateMonitorConnection)
  window.addEventListener('focus', revalidateMonitorConnection)
  window.addEventListener('online', revalidateMonitorConnection)
  connectMonitor()
})

onUnmounted(() => {
  isMonitorUnmounted = true
  removeBrowserExitListeners()
  if (monitorReconnectTimer !== null) {
    window.clearTimeout(monitorReconnectTimer)
    monitorReconnectTimer = null
  }
  void webphoneRef.value?.stop()
  window.removeEventListener('webphone-manual-call', handleManualCallEvent as EventListener)
  window.removeEventListener(
    'direct-call-campaign-selected',
    handleDirectCallCampaignSelected as EventListener,
  )
  window.removeEventListener('direct-call-view-closed', handleDirectCallViewClosed)
  window.removeEventListener('calloperator:webphone-logout', handleWebphoneLogout)
  document.removeEventListener('visibilitychange', restoreWebphoneFromBrowserTab)
  window.removeEventListener('focus', restoreWebphoneFromBrowserTab)
  document.removeEventListener('visibilitychange', revalidateMonitorConnection)
  window.removeEventListener('focus', revalidateMonitorConnection)
  window.removeEventListener('online', revalidateMonitorConnection)
  stopMonitorHeartbeat()
  isIntentionalMonitorClose = true
  monitorSocket.value?.close()
  monitorSocket.value = null
  setMonitorSipConnected(true)
  setWebphoneSipConnected(true)
  void callAiStore.stopAutoTranscription()
  callAiStore.endCallSession()
  resetWebphoneCallState()
  disconnectLargeCallViewViewportListener()
  disconnectSmallWebphoneViewportListener()
  disconnectWebphoneObserver()
})

const handleManualCallEvent = async (event: Event) => {
  const customEvent = event as CustomEvent<{
    phoneNumber?: string
    campaignId?: string
    contactId?: string
  }>
  const phoneNumber = customEvent.detail?.phoneNumber?.trim()

  if (!phoneNumber) return
  if (!(await validateOutboundCallPrerequisites())) return

  // Las llamadas iniciadas desde una campaña ya traen el contexto elegido.
  // El selector solo es necesario para números digitados en el webphone.
  const campaignId = customEvent.detail?.campaignId
  let contactId = customEvent.detail?.contactId

  if (campaignId) {
    try {
      const contact = await ensureContactForCampaign(phoneNumber, campaignId, contactId)
      contactId = contact.id
      workspace.setContact(contact)
      workspace.setContactPhone(normalizePhone(phoneNumber))

      try {
        if (authStore.extensionNumber === 'N/A') {
          // Extensión del operador todavía no cargó (useAuthStore.ts:89).
          // No bloqueamos la llamada por esto, pero registrar el intent con
          // "N/A" como extensión lo vuelve inútil (nebula_manual_intent.py
          // busca por extensión real) - mejor dejar rastro y omitirlo.
          console.warn(
            '[WebphoneDraggable] extensión del operador no cargada, se omite registro de intent de llamada manual',
            { campaignId, phone: normalizePhone(phoneNumber) },
          )
        } else {
          await registerManualCallIntent(
            {
              extension: authStore.extensionNumber,
              campaign_id: campaignId,
              phone: normalizePhone(phoneNumber),
              contact_id: contactId,
            },
            { notify: false },
          )
        }
      } catch (error) {
        // El fallo del intent no debe impedir que la llamada salga.
        console.error('[WebphoneDraggable] registro de intento de llamada manual falló:', error)
      }
    } catch {
      // El servicio ya informa el error. Sin contacto no se inicia la llamada.
      return
    }
  }

  markerDialContext.value = campaignId
    ? {
        phone: normalizePhone(phoneNumber),
        campaignId,
      }
    : null
  workspace.setCallMaskNumber(workspace.getCampaignMaskNumber(campaignId))
  isOutboundDialStarting.value = true
  outboundDisplayNumber.value = normalizePhone(phoneNumber)
  manualDialTarget.value = phoneNumber
  manualDialToken.value += 1
  authStore.setShowWebphone(true)
  void searchManualCallContact(phoneNumber, campaignId, contactId)
}

const normalizePhone = (value: string) => value.replace(/[^\d+]/g, '').replace(/^\+/, '')

// @tmsoft/webphone conserva el remoteDisplayName de una llamada entrante en el
// canal SIP después de finalizarla. Al marcar luego un número, ese nombre viejo
// puede aparecer encima del destino nuevo. Registrar temporalmente el destino
// como contacto hace que la librería resuelva ambos textos al número actual.
const outboundWebphoneContacts = computed(() => {
  const phone = normalizePhone(outboundDisplayNumber.value)
  return phone ? [{ name: phone, number: phone, type: 'contact' as const }] : []
})

const ensureContactForCampaign = async (
  rawPhone: string,
  campaignId: string,
  contactId?: string,
) => {
  const phone = normalizePhone(rawPhone)

  if (
    workspace.contact?.campaign_id === campaignId &&
    normalizePhone(workspace.contact.phone) === phone &&
    (!contactId || workspace.contact.id === contactId)
  ) {
    return workspace.contact
  }

  if (contactId) {
    try {
      const contact = await getContactById(contactId)
      if (contact.campaign_id === campaignId) return contact
    } catch {
      // Si el ID ya no existe, se resuelve nuevamente por teléfono y campaña.
    }
  }

  try {
    return await searchContact({ phone, campaign_id: campaignId }, { showErrorToast: false })
  } catch (error) {
    if (isAxiosError(error) && error.response?.status !== 404) throw error

    return await createContact(
      {
        name: phone,
        phone,
        campaign_id: campaignId,
        custom_fields: [],
      },
      { showSuccessToast: false },
    )
  }
}

const loadIncomingCallContext = async (context: WebPhoneCallContext) => {
  const queueId = context.queue?.id?.trim()
  const phone = normalizePhone(context.remoteNumber || '')

  if (context.direction !== 'incoming' || !queueId || !phone) return

  const inviteKey = `${context.sipCallId || context.channelId}:${queueId}:${phone}`
  const callId = incomingCallSessionKey(context)
  if (lastIncomingInviteKey.value === inviteKey) return
  lastIncomingInviteKey.value = inviteKey
  pendingIncomingQueueCall.value = null

  authStore.setShowWebphone(true)
  workspace.setWebphonePipExpanded(true)
  workspace.setContactPhone(phone)
  workspace.setContact(null)

  try {
    const { campaign_id: campaignId } = await getCampaignByQueueId(queueId)
    if (lastIncomingInviteKey.value !== inviteKey) return

    // Se prepara el contexto desde el INVITE, pero el screen pop espera a que
    // el agente conteste y el webphone emita el estado `connected`.
    pendingIncomingQueueCall.value = {
      callId,
      campaignId,
      returnRoute: route.fullPath,
    }
    void navigateToAnsweredIncomingCampaign(callId)
    if (lastIncomingInviteKey.value !== inviteKey) return

    const contact = await ensureContactForCampaign(phone, campaignId)
    if (lastIncomingInviteKey.value !== inviteKey) return

    workspace.setContact(contact)
  } catch (error) {
    console.error(
      '[WebphoneDraggable] no fue posible cargar la campaña y el contacto del INVITE:',
      error,
    )
  }
}

const navigateToAnsweredIncomingCampaign = async (callId: string) => {
  const incomingCall = pendingIncomingQueueCall.value
  if (
    answeredIncomingCallKey.value !== callId ||
    !incomingCall ||
    incomingCall.callId !== callId ||
    lastAnsweredIncomingNavigationKey.value === callId
  ) {
    return
  }

  lastAnsweredIncomingNavigationKey.value = callId
  await navigateToDialingCampaign(incomingCall.campaignId, incomingCall.returnRoute)
}

const ensureContactRequestViewForCall = async (event: WebPhoneCallStateEvent) => {
  const phone = normalizePhone(
    event.remoteNumber || workspace.contactPhone || manualDialTarget.value,
  )
  const currentCampaignId = workspace.callCampaignId || workspace.selectedCampaignId
  const navigationKey = `${event.sipCallId || event.channelId || phone}:${
    currentCampaignId || 'pending'
  }`

  if (lastCallNavigationKey.value === navigationKey || isCallNavigationInProgress.value) return

  lastCallNavigationKey.value = navigationKey
  isCallNavigationInProgress.value = true

  if (phone) {
    workspace.setContactPhone(phone)
  }

  try {
    if ((isDirectCallSelectionActive.value || route.name === 'direct-call') && !currentCampaignId) {
      return
    }

    if (phone && (!workspace.contact || normalizePhone(workspace.contact.phone) !== phone)) {
      try {
        const contact = currentCampaignId
          ? await ensureContactForCampaign(phone, currentCampaignId)
          : await searchContact({ phone }, { showErrorToast: false })
        workspace.setContact(contact)
      } catch {
        workspace.setContact(null)
      }
    }

    const campaignId = workspace.contact?.campaign_id || currentCampaignId
    if (!campaignId) return

    const isAlreadyInTargetRoute =
      route.name === 'campaign-detail' && String(route.params.id ?? '') === campaignId

    if (!isAlreadyInTargetRoute) {
      await navigateToDialingCampaign(campaignId)
    }
  } finally {
    isCallNavigationInProgress.value = false
  }
}

const navigateToDialingCampaign = async (campaignId: string, returnRouteOverride?: string) => {
  workspace.selectCampaign(campaignId)
  const targetRoute = { name: 'campaign-detail' as const, params: { id: campaignId } }
  const isAlreadyInTargetRoute =
    route.name === 'campaign-detail' && String(route.params.id ?? '') === campaignId
  const returnRoute =
    returnRouteOverride ?? (isAlreadyInTargetRoute ? workspace.callReturnRoute : route.fullPath)

  try {
    const campaign = await getCampaignById(campaignId)
    workspace.setSelectedCampaign(campaign)
  } catch {
    workspace.setSelectedCampaign(null)
  }

  workspace.setCallNavigationContext({
    campaignId,
    returnRoute,
    useIa: workspace.getCampaignUseIa(campaignId),
    maskNumber: workspace.getCampaignMaskNumber(campaignId),
  })

  if (!isAlreadyInTargetRoute) {
    await router.push(targetRoute)
  }
}

const searchManualCallContact = async (
  rawPhone: string,
  fallbackCampaignId?: string,
  contactId?: string,
) => {
  const phone = normalizePhone(rawPhone)
  if (!phone) return

  workspace.setContactPhone(phone)

  try {
    const contact = fallbackCampaignId
      ? await ensureContactForCampaign(phone, fallbackCampaignId, contactId)
      : contactId
        ? await getContactById(contactId)
        : await searchContact({ phone }, { showErrorToast: false })
    workspace.setContact(contact)

    const campaignId = fallbackCampaignId || contact.campaign_id
    if (campaignId) {
      await navigateToDialingCampaign(campaignId)
    }
  } catch {
    workspace.setContact(null)

    if (fallbackCampaignId) {
      await navigateToDialingCampaign(fallbackCampaignId)
    }
  }
}

const setNativeInputValue = (input: HTMLInputElement, value: string) => {
  const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
  valueSetter?.call(input, value)
  input.dispatchEvent(
    new InputEvent('input', { bubbles: true, inputType: 'insertText', data: value }),
  )
  input.dispatchEvent(new Event('change', { bubbles: true }))
}

const getWebphoneRoot = () =>
  (containerRef.value?.querySelector('.webphone-draggable') as HTMLElement | null) ??
  document.querySelector<HTMLElement>('.webphone-draggable')

const getWebphoneInput = () =>
  getWebphoneRoot()?.querySelector<HTMLInputElement>(
    'input.webphone-display__input, input.webphone-actions__input',
  ) ?? null

const getManualDialSuggestion = () =>
  getWebphoneRoot()?.querySelector<HTMLElement>('.webphone-display__suggestion--raw') ?? null

const getWebphoneCallButton = () =>
  getWebphoneRoot()?.querySelector<HTMLButtonElement>('button.webphone-actions__button--call') ??
  null

const getWebphoneManualDialSuggestion = () =>
  getWebphoneRoot()?.querySelector<HTMLElement>('.webphone-display__suggestion--raw') ?? null

const getWebphoneActionButton = (selectors: string) =>
  getWebphoneRoot()?.querySelector<HTMLButtonElement>(selectors) ?? null

const triggerWebphoneAction = (selectors: string) => {
  const button = getWebphoneActionButton(selectors)

  if (button && !button.disabled) {
    button.click()
    return
  }

  workspace.setWebphonePipExpanded(true)
  authStore.setShowWebphone(true)
}

const notifyOutboundCallDisabled = () => {
  toast.add({
    severity: 'warn',
    summary: t('general.warning'),
    detail: outboundCallDisabledMessage.value,
    life: 3000,
  })
}

const validateOutboundCallPrerequisites = async () => {
  if (
    isExtensionActiveElsewhere.value ||
    !hasAssignedExtension.value ||
    !authStore.webphoneAvailable ||
    isWebphoneDisconnected.value
  ) {
    notifyOutboundCallDisabled()
    syncOutboundCallAvailability()
    return false
  }

  const microphoneAccess = await ensureMicrophoneAccess()
  syncOutboundCallAvailability()

  if (microphoneAccess.allowed) return true

  notifyOutboundCallDisabled()
  return false
}

const syncOutboundCallAvailability = () => {
  const callButton = getWebphoneCallButton()
  const manualDialSuggestion = getWebphoneManualDialSuggestion()
  const disabled = isOutboundCallDisabled.value

  // El WebPhone es de libreria y re-renderiza su DOM interno; sincronizamos
  // atributos en cada cambio observado para impedir llamadas sin telefonia lista.
  if (callButton) {
    callButton.disabled = disabled
    callButton.setAttribute('aria-disabled', String(disabled))
    callButton.title = disabled ? outboundCallDisabledMessage.value : ''
    callButton.classList.toggle('calloperator-webphone-call-disabled', disabled)
  }

  if (manualDialSuggestion) {
    manualDialSuggestion.setAttribute('aria-disabled', String(disabled))
    manualDialSuggestion.setAttribute('title', disabled ? outboundCallDisabledMessage.value : '')
    manualDialSuggestion.classList.toggle('calloperator-webphone-call-disabled', disabled)
  }
}

const answerCompactCall = () => {
  triggerWebphoneAction(
    'button.webphone-actions__button--answer, button.webphone-actions__button--call',
  )
}

const hangupCompactCall = () => {
  triggerWebphoneAction(
    'button.webphone-actions__button--hangup, button.webphone-actions__button--reject, button.webphone-actions__button--cancel',
  )
}

const hangupActiveCall = () => {
  getWebphoneActionButton(
    'button.webphone-actions__button--hangup, button.webphone-actions__button--reject, button.webphone-actions__button--cancel',
  )?.click()
}

const hangupActiveCallBeforeExit = () => {
  if (unloadHangupTriggered || !hasActiveWebphoneCall.value) return

  unloadHangupTriggered = true
  hangupActiveCall()
}

const stopActiveAudioTracks = () => {
  const streams = [
    window.__callOperatorWebphoneMedia?.localStream,
    window.__callOperatorWebphoneMedia?.remoteStream,
  ]

  streams.forEach((stream) => stream?.getAudioTracks().forEach((track) => track.stop()))
  window.__callOperatorWebphoneMedia = undefined
  webphoneDebug.localTracks = []
  webphoneDebug.remoteTracks = []
}

const releaseWebphoneForLogout = async () => {
  isWebphoneLogoutInProgress = true
  setMonitorSipConnected(true)
  setWebphoneSipConnected(true)

  if (hasActiveWebphoneCall.value) {
    hangupActiveCall()
    await nextTick()
  }

  // stop() cierra la sesión SIP y la RTCPeerConnection que administra el WebPhone.
  try {
    await webphoneRef.value?.stop()
  } finally {
    stopActiveAudioTracks()
    try {
      await callAiStore.stopAutoTranscription()
    } finally {
      callAiStore.endCallSession()
      workspace.setWebphoneCallState({ status: 'idle', duration: '00:00' })
    }
  }
}

const handleWebphoneLogout = (event: Event) => {
  const detail = (event as CustomEvent<{ handled: boolean; complete: () => void }>).detail
  detail.handled = true
  isIntentionalMonitorClose = true
  monitorSocket.value?.close()
  monitorSocket.value = null
  void releaseWebphoneForLogout()
    .catch((error: unknown) => {
      console.error('[WebphoneDraggable] error al liberar recursos durante el logout:', error)
    })
    .finally(() => {
      setMonitorSipConnected(true)
      setWebphoneSipConnected(true)
      detail.complete()
    })
}

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (!hasActiveWebphoneCall.value) return

  event.preventDefault()
  event.returnValue = ACTIVE_CALL_UNLOAD_WARNING
}

const handlePageExit = () => {
  hangupActiveCallBeforeExit()
}

const addBrowserExitListeners = () => {
  if (browserExitListenersAttached) return

  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('pagehide', handlePageExit)
  window.addEventListener('unload', handlePageExit)
  browserExitListenersAttached = true
}

const removeBrowserExitListeners = () => {
  if (!browserExitListenersAttached) return

  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('pagehide', handlePageExit)
  window.removeEventListener('unload', handlePageExit)
  browserExitListenersAttached = false
}

watch(
  hasActiveWebphoneCall,
  (hasActiveCall) => {
    if (hasActiveCall) {
      unloadHangupTriggered = false
      addBrowserExitListeners()
      return
    }

    removeBrowserExitListeners()
  },
  { flush: 'sync' },
)

const toggleCompactMute = () => {
  triggerWebphoneAction('button.webphone-actions__button--mute')
  isCompactMuted.value = !isCompactMuted.value
}

const toggleCompactHold = () => {
  triggerWebphoneAction('button.webphone-actions__button--hold')
  isCompactHeld.value = !isCompactHeld.value
}

const transferCompactCall = () => {
  triggerWebphoneAction('button.webphone-actions__button--transfer')
}

const expandCompactWebphone = () => {
  workspace.setWebphonePipExpanded(true)
  authStore.setShowWebphone(true)
}

const isWebphoneDialInput = (target: EventTarget | null): target is HTMLInputElement =>
  target instanceof HTMLInputElement &&
  target.matches('input.webphone-display__input, input.webphone-actions__input')

const handleDirectCallInitiation = (event: MouseEvent | KeyboardEvent) => {
  const target = event.target as HTMLElement | null
  const isEnterFromDialInput =
    event instanceof KeyboardEvent && event.key === 'Enter' && isWebphoneDialInput(target)
  const callElement = isEnterFromDialInput
    ? target
    : target?.closest<HTMLElement>(
        'button.webphone-actions__button--call, .webphone-display__suggestion--raw',
      )
  if (!callElement) return

  if (isOutboundCallDisabled.value) {
    event.preventDefault()
    event.stopPropagation()
    notifyOutboundCallDisabled()
    syncOutboundCallAvailability()
    return
  }

  const dialedPhone = normalizePhone(getWebphoneInput()?.value || '')
  if (!dialedPhone) return

  // Se marca antes de que el WebPhone emita `dialing`. Así, un cambio de ruta
  // causado por la selección de campaña no interpreta este intervalo como idle
  // ni oculta/desmonta visualmente el teléfono antes de iniciar la llamada.
  isOutboundDialStarting.value = true
  outboundDisplayNumber.value = dialedPhone
  authStore.setShowWebphone(true)
  workspace.setContactPhone(dialedPhone)
  if (workspace.contact && normalizePhone(workspace.contact.phone) !== dialedPhone) {
    workspace.setContact(null)
  }

  const markerContext = markerDialContext.value
  if (
    markerContext &&
    (!dialedPhone || markerContext.phone === dialedPhone) &&
    markerContext.campaignId
  ) {
    isDirectWebphoneDialPending.value = false
    return
  }

  // La vista cambia después de que el webphone procesa el evento, por lo que
  // la llamada siempre sale y la campaña puede asociarse desde la cabecera.
  isDirectWebphoneDialPending.value = true
  openDirectCallView(dialedPhone)
}

const associateDirectCallContact = async (campaignId: string) => {
  const phone = normalizePhone(workspace.contactPhone || webphoneDebug.remoteNumber)
  if (!phone) return null

  let contact =
    workspace.contact &&
    workspace.contact.campaign_id === campaignId &&
    normalizePhone(workspace.contact.phone) === phone
      ? workspace.contact
      : null

  contact ||= await ensureContactForCampaign(phone, campaignId)
  workspace.setContact(contact)
  return contact
}

const handleDirectCallViewClosed = () => {
  isDirectCallSelectionActive.value = false
  isDirectWebphoneDialPending.value = false
}

const handleDirectCallCampaignSelected = async (event: Event) => {
  const detail = (
    event as CustomEvent<{
      campaignId?: string
      onComplete?: (success: boolean) => void
    }>
  ).detail
  const campaignId = detail?.campaignId
  if (!campaignId) {
    detail?.onComplete?.(false)
    return
  }

  try {
    const contact = await associateDirectCallContact(campaignId)
    if (!contact) {
      detail?.onComplete?.(false)
      return
    }

    try {
      if (authStore.extensionNumber === 'N/A') {
        // Extensión del operador todavía no cargó (useAuthStore.ts:89).
        // No interrumpimos la llamada por esto, pero registrar el intent con
        // "N/A" como extensión lo vuelve inútil (nebula_manual_intent.py
        // busca por extensión real) - mejor dejar rastro y omitirlo.
        console.warn(
          '[WebphoneDraggable] extensión del operador no cargada, se omite registro de intent de llamada manual',
          { campaignId, phone: contact.phone },
        )
      } else {
        await registerManualCallIntent(
          {
            extension: authStore.extensionNumber,
            campaign_id: campaignId,
            phone: contact.phone,
            contact_id: contact.id,
          },
          { notify: false },
        )
      }
    } catch (error) {
      // El fallo del intent no debe interrumpir una llamada que ya fue iniciada.
      console.error('[WebphoneDraggable] registro de intento de llamada manual falló:', error)
    }

    markerDialContext.value = {
      phone: normalizePhone(contact.phone),
      campaignId,
    }
    await navigateToDialingCampaign(
      campaignId,
      workspace.callReturnRoute || directCallReturnRoute.value,
    )
    isDirectCallSelectionActive.value = false
    isDirectWebphoneDialPending.value = false
    detail?.onComplete?.(true)
  } catch {
    // El servicio muestra el error y la vista conserva el selector para reintentar.
    detail?.onComplete?.(false)
  }
}

const isWebphoneCallStatus = (
  status: string,
): status is 'idle' | 'calling' | 'incoming' | 'connected' | 'held' =>
  ['idle', 'calling', 'incoming', 'connected', 'held'].includes(status)

const normalizeStatusLabel = (label: string) =>
  label
    .replace(/\s+/g, ' ')
    .replace(/^Conectado$/i, 'En llamada')
    .trim()

const isDisconnectedWebphoneLabel = (label: string) => {
  const normalized = label.toLowerCase()
  return (
    normalized.includes('sin conexión') ||
    normalized.includes('sin conexion') ||
    normalized.includes('sin internet') ||
    normalized.includes('desconect') ||
    normalized.includes('disconnected')
  )
}

const getWebphoneCallState = () => {
  const root = getWebphoneRoot()
  const webphone = root?.querySelector<HTMLElement>('.webphone')
  const display = root?.querySelector<HTMLElement>('.webphone-display')
  const rawStatus =
    display?.dataset.callStatus ||
    webphone?.dataset.callStatus ||
    root?.dataset.callStatus ||
    'idle'
  const status = isWebphoneCallStatus(rawStatus) ? rawStatus : 'idle'
  const duration =
    root?.querySelector<HTMLElement>('.webphone-display__duration')?.textContent?.trim() || '00:00'
  const label =
    root
      ?.querySelector<HTMLElement>('.webphone-display__connected-status-text')
      ?.textContent?.trim() ||
    root?.querySelector<HTMLElement>('.webphone-display__status-text')?.textContent?.trim() ||
    ''

  return {
    status,
    label: normalizeStatusLabel(label),
    duration,
  }
}

const syncWebphoneCallState = () => {
  syncOutboundCallAvailability()

  if (isWebphoneLogoutInProgress || !authStore.isAuthenticated) {
    setWebphoneSipConnected(true)
    resetWebphoneCallState()
    return
  }

  const callState = getWebphoneCallState()
  setWebphoneSipConnected(!isDisconnectedWebphoneLabel(callState.label))
  workspace.setWebphoneCallState(callState)

  if (callState.status === 'idle') {
    isCompactMuted.value = false
    isCompactHeld.value = false
  } else if (callState.status === 'held') {
    isCompactHeld.value = true
  }

  if (callState.status === 'incoming') {
    notifyIncomingCall({
      phoneNumber: webphoneDebug.remoteNumber,
      callId:
        webphoneDebug.sipCallId ||
        String(webphoneDebug.channelId || webphoneDebug.remoteNumber || 'incoming'),
    })
  }
}

const disconnectWebphoneObserver = () => {
  webphoneObserver?.disconnect()
  webphoneObserver = null
  if (webphoneObserverRetryTimer !== null) {
    window.clearTimeout(webphoneObserverRetryTimer)
    webphoneObserverRetryTimer = null
  }
  webphoneEventRoot?.removeEventListener('click', handleDirectCallInitiation, true)
  webphoneEventRoot?.removeEventListener('keydown', handleDirectCallInitiation, true)
  webphoneEventRoot = null
}

const syncWebphoneInteractionListeners = () => {
  const root = getWebphoneRoot()

  if (webphoneEventRoot && webphoneEventRoot !== root) {
    webphoneEventRoot.removeEventListener('click', handleDirectCallInitiation, true)
    webphoneEventRoot.removeEventListener('keydown', handleDirectCallInitiation, true)
    webphoneEventRoot = null
  }

  if (!root || !shouldDisplayWebphone.value) {
    webphoneEventRoot?.removeEventListener('click', handleDirectCallInitiation, true)
    webphoneEventRoot?.removeEventListener('keydown', handleDirectCallInitiation, true)
    webphoneEventRoot = null
    return
  }

  if (webphoneEventRoot === root) return

  webphoneEventRoot = root
  root.addEventListener('click', handleDirectCallInitiation, true)
  root.addEventListener('keydown', handleDirectCallInitiation, true)
}

const scheduleWebphoneObserverRetry = () => {
  if (
    webphoneObserverRetryTimer !== null ||
    isWebphoneLogoutInProgress ||
    !authStore.isAuthenticated ||
    isExtensionActiveElsewhere.value
  ) {
    return
  }

  webphoneObserverRetryTimer = window.setTimeout(() => {
    webphoneObserverRetryTimer = null
    void connectWebphoneObserver()
  }, 500)
}

const connectWebphoneObserver = async () => {
  if (isExtensionActiveElsewhere.value) {
    disconnectWebphoneObserver()
    setWebphoneSipConnected(true)
    return
  }

  if (webphoneObserver) {
    syncWebphoneInteractionListeners()
    syncWebphoneCallState()
    return
  }

  await nextTick()
  await wait(250)

  const root = getWebphoneRoot()
  if (!root) {
    // El WebPhone aún no existe mientras la vista está montando; evitar alertas
    // falsas hasta que el componente pinte su estado real.
    setWebphoneSipConnected(true)
    scheduleWebphoneObserverRetry()
    return
  }

  syncWebphoneCallState()
  webphoneObserver = new MutationObserver(syncWebphoneCallState)
  webphoneObserver.observe(root, {
    attributes: true,
    attributeFilter: ['class', 'data-call-status'],
    childList: true,
    subtree: true,
    characterData: true,
  })
  syncWebphoneInteractionListeners()
}

const syncTargetToWebphone = async (target: string) => {
  if (isOutboundCallDisabled.value) {
    syncOutboundCallAvailability()
    return
  }

  for (let attempt = 0; attempt < 10; attempt += 1) {
    await nextTick()
    await wait(attempt === 0 ? 250 : 200)

    const input = getWebphoneInput()
    if (!input) continue

    input.focus()
    setNativeInputValue(input, target)

    await wait(120)

    const callButton = getWebphoneCallButton()
    if (callButton && !callButton.disabled) {
      callButton.click()
      return
    }

    const manualDialSuggestion = getManualDialSuggestion()
    if (manualDialSuggestion) {
      manualDialSuggestion.dispatchEvent(new PointerEvent('pointerdown', { bubbles: false }))
      manualDialSuggestion.dispatchEvent(new PointerEvent('pointerup', { bubbles: false }))
      return
    }
  }
}

const phonePosition = ref({ x: 86, y: 100 })
const compactWebphonePosition = ref({ x: 24, y: 24 })
const compactCallTitle = computed(() => {
  if (workspace.webphoneCallStatus === 'incoming') return t('contact_info.incoming_call')
  if (workspace.webphoneCallStatus === 'calling') return t('contact_info.calling')
  if (workspace.webphoneCallStatus === 'held') return t('contact_info.on_hold')
  return t('contact_info.in_call')
})
const compactCallNumber = computed(
  () => webphoneDebug.remoteNumber || workspace.contactPhone || workspace.contact?.phone || '',
)

const getPhonePosition = (sidebarExpanded: boolean, registeredInCampaign: boolean) => {
  if (isSmallWebphoneViewport.value) {
    return {
      x: 16,
      y: 84,
    }
  }

  const sidebarWidth = sidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH
  return {
    x: sidebarWidth + WEBPHONE_SIDEBAR_OFFSET,
    y: WEBPHONE_TOP_OFFSET + (registeredInCampaign ? 0 : WEBPHONE_CAMPAIGN_ALERT_OFFSET),
  }
}

const getCompactWebphonePosition = () => {
  if (typeof window === 'undefined') return { x: 24, y: 24 }

  return {
    x: Math.max(window.innerWidth - 380, 16),
    y: Math.max(window.innerHeight - 150, 100),
  }
}

watch(
  [() => props.sidebarExpanded, isRegisteredInCampaign, isSmallWebphoneViewport],
  ([sidebarExpanded, registeredInCampaign]) => {
    phonePosition.value = getPhonePosition(sidebarExpanded, registeredInCampaign)
    compactWebphonePosition.value = getCompactWebphonePosition()
  },
  { immediate: true },
)

watch(
  [() => authStore.accessToken, extensionNumber, isExtensionActiveElsewhere],
  ([token, extension, extensionIsActive], previous) => {
    const [previousToken, previousExtension, previousExtensionIsActive] = previous ?? []
    if (
      token === previousToken &&
      extension === previousExtension &&
      extensionIsActive === previousExtensionIsActive
    ) {
      return
    }

    isIntentionalMonitorClose = !token || !authStore.isAuthenticated || extensionIsActive
    monitorSocket.value?.close()
    monitorSocket.value = null
    if (monitorReconnectTimer !== null) {
      window.clearTimeout(monitorReconnectTimer)
      monitorReconnectTimer = null
    }
    connectMonitor()
    disconnectWebphoneObserver()
    if (token && extension && authStore.isAuthenticated && !extensionIsActive) {
      void connectWebphoneObserver()
    } else {
      setWebphoneSipConnected(true)
    }
  },
)

watch(
  () =>
    Boolean(
      authStore.isAuthenticated &&
        authStore.accessToken &&
        extensionNumber.value &&
        !isExtensionActiveElsewhere.value,
    ),
  (canObserveWebphone) => {
    if (canObserveWebphone) {
      void connectWebphoneObserver()
      return
    }

    disconnectWebphoneObserver()
    setWebphoneSipConnected(true)
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => [shouldDisplayWebphone.value, manualDialToken.value, manualDialTarget.value] as const,
  async ([isOpen, dialToken, target]) => {
    const normalizedTarget = target?.trim()
    if (!isOpen || !normalizedTarget) return

    const dialKey = `${dialToken}:${normalizedTarget}`
    if (lastDialAttemptKey.value === dialKey) return

    lastDialAttemptKey.value = dialKey
    await syncTargetToWebphone(normalizedTarget)
  },
  { flush: 'post' },
)

watch(
  shouldDisplayWebphone,
  (isOpen) => {
    syncWebphoneInteractionListeners()
    syncOutboundCallAvailability()

    if (!isOpen && !hasActiveWebphoneCall.value) {
      resetWebphoneCallState()
    }
  },
  { immediate: true, flush: 'post' },
)

watch(
  [
    hasAssignedExtension,
    () => authStore.webphoneAvailable,
    () => connectivityStore.isAnyServiceDisconnected,
    microphonePermission,
  ],
  () => {
    syncOutboundCallAvailability()
  },
  { flush: 'post' },
)

watch(
  [() => route.name, hasVoiceCampaign],
  ([routeName, canUseVoice]) => {
    const isHomeRoute = homeRouteNames.has(String(routeName ?? ''))

    if (!isHomeRoute) {
      hasAutoOpenedInHome.value = false
      return
    }

    if (!hasAutoOpenedInHome.value && canUseVoice) {
      authStore.setShowWebphone(true)
      hasAutoOpenedInHome.value = true
    }
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  () => {
    const shouldKeepIncomingCallExpanded =
      webphoneDebug.direction === 'incoming' &&
      (hasActiveWebphoneCall.value || Boolean(lastIncomingInviteKey.value))

    // El screen pop cambia la ruta al contestar una llamada entrante. Ese cambio
    // no debe contraer el webphone que acabamos de abrir.
    if (shouldKeepIncomingCallExpanded) {
      authStore.setShowWebphone(true)
      workspace.setWebphonePipExpanded(true)
      return
    }

    workspace.setWebphonePipExpanded(false)
    if (
      !shouldPreferFullWebphoneRoute.value &&
      !hasActiveWebphoneCall.value &&
      !isOutboundDialStarting.value
    ) {
      authStore.setShowWebphone(false)
    }
  },
)
</script>

<template>
  <div ref="containerRef">
    <UseDraggable
      v-if="extensionDominio && !isExtensionActiveElsewhere"
      v-show="shouldShowFullWebphone"
      :initial-value="{ x: 100, y: 100 }"
      class="webphone-draggable fixed z-50"
    >
      <WebPhone
        ref="webphoneRef"
        :early-media="true"
        :extension="extensionNumber"
        :password="extensionPassword"
        :host="extensionDominio"
        :domain="extensionDominio"
        mode="call"
        :position="phonePosition"
        :initial-keypad-visible="false"
        :mask-number="shouldMaskWebphoneNumber"
        :contacts="outboundWebphoneContacts"
        @call-context="handleCallContext"
        @call-state="handleCallState"
        @media-ready="handleMediaReady"
      />
    </UseDraggable>

    <UseDraggable
      v-if="shouldShowCompactWebphone"
      :initial-value="compactWebphonePosition"
      prevent-default
      class="webphone-pip backdrop-blur-2xl fixed z-[50] w-[min(22rem,calc(100vw-2rem))] rounded-2xl app-glass border border-white/70 dark:!border-[var(--p-primary-400)]/30 p-3.5 shadow-xl text-[var(--p-text-color)]"
    >
      <aside aria-live="polite">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="truncate text-xs font-semibold uppercase text-[var(--p-primary-color)]">
              {{ compactCallTitle }}
            </p>
            <p class="truncate text-sm font-semibold">
              {{
                compactCallNumber || workspace.webphoneCallStatusLabel || t('contact_info.phone')
              }}
            </p>
            <p class="text-xs text-[var(--p-text-muted-color)]">
              {{ workspace.webphoneCallDuration }}
            </p>
          </div>

          <button
            type="button"
            class="webphone-pip__icon-button cursor-pointer"
            aria-label="Expandir webphone"
            @click="expandCompactWebphone"
          >
            <Maximize2 class="h-4 w-4" />
          </button>
        </div>

        <div class="mt-3 grid grid-cols-4 gap-2">
          <button
            v-if="workspace.webphoneCallStatus === 'incoming'"
            type="button"
            class="webphone-pip__action webphone-pip__action--answer"
            aria-label="Contestar"
            @click="answerCompactCall"
          >
            <Phone class="h-4 w-4" />
          </button>

          <button
            v-if="workspace.webphoneCallStatus !== 'incoming'"
            type="button"
            class="webphone-pip__action"
            :class="{ 'webphone-pip__action--muted': isCompactMuted }"
            :aria-label="isCompactMuted ? 'Activar micrófono' : 'Silenciar'"
            @click="toggleCompactMute"
          >
            <MicOff v-if="isCompactMuted" class="h-4 w-4" />
            <Mic v-else class="h-4 w-4" />
          </button>

          <button
            v-if="workspace.webphoneCallStatus !== 'incoming'"
            type="button"
            class="webphone-pip__action"
            :class="{ 'webphone-pip__action--held': isCompactHeld }"
            :aria-label="isCompactHeld ? 'Reanudar' : 'Espera'"
            @click="toggleCompactHold"
          >
            <Play v-if="isCompactHeld" class="h-4 w-4" />
            <Pause v-else class="h-4 w-4" />
          </button>

          <button
            v-if="workspace.webphoneCallStatus !== 'incoming'"
            type="button"
            class="webphone-pip__action webphone-pip__action--transfer"
            aria-label="Transferir"
            @click="transferCompactCall"
          >
            <ArrowRightLeft class="h-4 w-4" />
          </button>

          <button
            type="button"
            class="webphone-pip__action webphone-pip__action--hangup"
            :class="{ 'col-span-3': workspace.webphoneCallStatus === 'incoming' }"
            aria-label="Colgar"
            @click="hangupCompactCall"
          >
            <PhoneOff class="h-4 w-4" />
          </button>
        </div>
      </aside>
    </UseDraggable>

    <!-- Debug STT chunks. To re-enable, uncomment this block and the helpers in script. NO ELIMINAR -->
    <!-- <aside
      v-if="showWebphoneDebug && debugAudioChunks.length > 0"
      class="fixed bottom-4 right-4 z-[70] max-h-[45vh] w-[min(92vw,720px)] overflow-auto rounded-md border border-slate-700 bg-slate-950/95 p-2 text-xs text-slate-100 shadow-xl"
      data-testid="webphone-audio-debug-panel"
    >
      <p class="mb-2 font-semibold text-emerald-400">Chunks STT</p>
      <table class="w-full border-collapse text-left">
        <thead class="sticky top-0 bg-slate-950 text-slate-300">
          <tr>
            <th class="border-b border-slate-700 px-2 py-1">Hora</th>
            <th class="border-b border-slate-700 px-2 py-1">Origen</th>
            <th class="border-b border-slate-700 px-2 py-1">Dur.</th>
            <th class="border-b border-slate-700 px-2 py-1">Peso</th>
            <th class="border-b border-slate-700 px-2 py-1">Estado</th>
            <th class="border-b border-slate-700 px-2 py-1">Texto</th>
            <th class="border-b border-slate-700 px-2 py-1"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="chunk in debugAudioChunks" :key="chunk.id">
            <td class="border-b border-slate-800 px-2 py-1 whitespace-nowrap">
              {{ formatDebugChunkTime(chunk.startedAt) }}
            </td>
            <td class="border-b border-slate-800 px-2 py-1">
              {{ chunk.party === 'agent' ? 'Agente' : 'Cliente' }}
            </td>
            <td class="border-b border-slate-800 px-2 py-1 whitespace-nowrap">
              {{ formatDebugChunkDuration(chunk.durationMs) }}
            </td>
            <td class="border-b border-slate-800 px-2 py-1 whitespace-nowrap">
              {{ formatDebugChunkSize(chunk.sizeBytes) }}
            </td>
            <td class="border-b border-slate-800 px-2 py-1">{{ chunk.status }}</td>
            <td class="max-w-[260px] truncate border-b border-slate-800 px-2 py-1">
              {{ chunk.text || chunk.errorMessage || '-' }}
            </td>
            <td class="border-b border-slate-800 px-2 py-1 text-right">
              <button
                type="button"
                class="rounded border border-slate-600 px-2 py-0.5 text-slate-100 hover:bg-slate-800"
                @click="downloadDebugChunk(chunk)"
              >
                WAV
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </aside> -->

    <!-- <aside
      v-if="showWebphoneDebug"
      class="fixed bottom-4 right-4 z-[70] w-[min(92vw,430px)] rounded-lg bg-slate-950/95 p-3 font-mono text-xs text-slate-100 shadow-xl"
      data-testid="webphone-debug-panel"
    >
      <p class="mb-2 font-bold text-emerald-400">WebPhone realtime debug</p>
      <dl class="grid grid-cols-[110px_1fr] gap-x-2 gap-y-1 break-all">
        <dt>state</dt>
        <dd>{{ webphoneDebug.state }}</dd>
        <dt>channel</dt>
        <dd>{{ webphoneDebug.channelId ?? '-' }}</dd>
        <dt>SIP Call-ID</dt>
        <dd>{{ webphoneDebug.sipCallId || '-' }}</dd>
        <dt>direction</dt>
        <dd>{{ webphoneDebug.direction || '-' }}</dd>
        <dt>remote</dt>
        <dd>{{ webphoneDebug.remoteNumber || '-' }}</dd>
        <dt>queue</dt>
        <dd>{{ webphoneDebug.queue || '-' }}</dd>
        <dt>local audio</dt>
        <dd>{{ webphoneDebug.localTracks.length }} track(s)</dd>
        <dt>remote audio</dt>
        <dd>{{ webphoneDebug.remoteTracks.length }} track(s)</dd>
        <dt>updated</dt>
        <dd>{{ webphoneDebug.updatedAt || '-' }}</dd>
      </dl>
      <details class="mt-2">
        <summary class="cursor-pointer text-sky-300">Tracks</summary>
        <p v-for="track in webphoneDebug.localTracks" :key="`local-${track}`">agent: {{ track }}</p>
        <p v-for="track in webphoneDebug.remoteTracks" :key="`remote-${track}`">
          customer: {{ track }}
        </p>
      </details>
    </aside> -->

    <Dialog
      v-model:visible="showEjectedDialog"
      modal
      :header="t('call.ejected_dialog_title')"
      :style="{ width: '26rem' }"
      :draggable="false"
      :closable="false"
    >
      <p class="m-0 text-sm leading-6 text-gray-700 dark:text-gray-200">
        {{ t('call.ejected_dialog_message') }}
      </p>

      <template #footer>
        <div class="flex justify-end">
          <AppButton
            :label="t('call.ejected_dialog_accept')"
            :block="false"
            @click="showEjectedDialog = false"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.cursor-move {
  cursor: move;
}

.webphone-draggable :deep(.webphone) {
  width: min(90vw, 360px) !important;
}

.webphone-draggable :deep(.calloperator-webphone-call-disabled) {
  cursor: not-allowed !important;
  opacity: 0.45 !important;
  pointer-events: none;
}

.webphone-pip {
  cursor: grab;
  touch-action: none;
  user-select: none;
  border-color: color-mix(in srgb, var(--p-primary-color) 24%, rgba(255, 255, 255, 0.7));
  box-shadow:
    0 16px 40px -18px color-mix(in srgb, var(--p-primary-color) 34%, transparent),
    0 8px 24px -12px rgba(15, 23, 42, 0.28);
}

.webphone-pip:active {
  cursor: grabbing;
}

.webphone-pip__icon-button,
.webphone-pip__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  border: 0;
  cursor: pointer;
  transition:
    transform 150ms ease,
    background-color 150ms ease,
    color 150ms ease;
}

.webphone-pip__icon-button {
  height: 2rem;
  width: 2rem;
  border-radius: 0.625rem;
  color: var(--p-text-muted-color);
}

.webphone-pip__icon-button:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--p-text-color);
}

.webphone-pip__action {
  height: 2.5rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--p-primary-color) 12%, transparent);
  color: var(--p-primary-color);
}

.webphone-pip__action:hover {
  transform: translateY(-1px);
  background: color-mix(in srgb, var(--p-primary-color) 20%, transparent);
}

.webphone-pip__action--answer {
  background: #16a34a;
  color: white;
}

.webphone-pip__action--answer:hover {
  background: #15803d;
}

.webphone-pip__action--held {
  background: #f59e0b;
  color: white;
}

.webphone-pip__action--held:hover {
  background: #d97706;
}

.webphone-pip__action--muted {
  background: #dc2626;
  color: white;
}

.webphone-pip__action--muted:hover {
  background: #b91c1c;
}

.webphone-pip__action--transfer {
  background: color-mix(in srgb, var(--p-primary-color) 18%, transparent);
}

.webphone-pip__action--transfer:hover {
  background: color-mix(in srgb, var(--p-primary-color) 28%, transparent);
}

.webphone-pip__action--hangup {
  background: #dc2626;
  color: white;
}

.webphone-pip__action--hangup:hover {
  background: #b91c1c;
}

:global(.dark) .webphone-pip__icon-button:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* Operational glass refinements for the embedded WebPhone component */
:deep(.webphone) {
  border-radius: 1.25rem !important;
  /* border: 1px solid color-mix(in srgb, var(--p-primary-color) 10%, rgba(255, 255, 255, 0.75)) !important; */
  box-shadow:
    0 4px 12px -12px color-mix(in srgb, var(--p-primary-color) 30%, transparent),
    0 4px 18px -2px rgba(15, 23, 42, 0.08) !important;
  backdrop-filter: blur(80px) saturate(120%) !important;
  -webkit-backdrop-filter: blur(14px) saturate(130%) !important;
}

:global(.dark .webphone) {
  border: 1px solid color-mix(in srgb, var(--p-primary-800) 34%, rgba(255, 255, 255, 0.08)) !important;
  background: color-mix(in srgb, var(--p-primary-950) 12%, transparent) !important;
  box-shadow:
    0 2px 6px -12px color-mix(in srgb, var(--p-primary-color) 42%, transparent),
    0 2px 6px -4px rgba(0, 0, 0, 0.4) !important;
}

/* Contact suggestions shown above the dialer input */
:global(.dark .webphone-display__suggestions) {
  color: rgb(226, 232, 240) !important;
  background: color-mix(in srgb, var(--p-primary-950) 100%, transparent) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 12px 28px -8px rgba(0, 0, 0, 0.55) !important;
}

:global(.dark .webphone-display__suggestion) {
  color: rgb(226, 232, 240) !important;
}

:global(.dark .webphone-display__suggestion:hover) {
  background: rgba(255, 255, 255, 0.08) !important;
}

:global(.dark .webphone-display__contact-name),
:global(.dark .webphone-display__contact-number) {
  color: rgb(226, 232, 240) !important;
}

:global(.dark .webphone-display__contact-number) {
  color: rgb(148, 163, 184) !important;
}

:deep(.webphone-keypad__key) {
  border-radius: 0.75rem !important;
  font-weight: 500 !important;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  background-color: rgba(0, 0, 0, 0.03) !important;
  transition: background-color 120ms ease, color 120ms ease, transform 120ms ease !important;
}

:global(.dark .webphone-keypad__key) {
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  background-color: rgba(255, 255, 255, 0.04) !important;
}

:deep(.webphone-keypad__key:hover) {
  background-color: color-mix(in srgb, var(--p-primary-color) 12%, transparent) !important;
  color: var(--p-primary-color) !important;
}

:deep(.webphone-actions__button--call) {
  background-image: none !important;
  background-color: var(--p-primary-color) !important;
  border-radius: 9999px !important;
  box-shadow: 0 2px 3px -2px color-mix(in srgb, var(--p-primary-color) 40%, transparent) !important;
}

:deep(.webphone-actions__button--call:hover) {
  background-color: color-mix(in srgb, var(--p-primary-color) 88%, black) !important;
}

:deep(.webphone-actions__button--hangup),
:deep(.webphone-actions__button--reject),
:deep(.webphone-actions__button--cancel) {
  background-image: none !important;
  background-color: #dc2626 !important;
  border-radius: 9999px !important;
}

:deep(.webphone-actions__button--hangup:hover),
:deep(.webphone-actions__button--reject:hover),
:deep(.webphone-actions__button--cancel:hover) {
  background-color: #b91c1c !important;
}

:deep(.webphone-actions__button--answer) {
  background-color: var(--p-primary-color) !important;
  border-radius: 9999px !important;
}

:deep(.webphone-actions__button--answer:hover) {
  background-color: color-mix(in srgb, var(--p-primary-color) 88%, black) !important;
}

:deep(.webphone-actions__button--hold) {
  border-radius: 9999px !important;
}

:deep(.webphone-actions__input) {
  border-radius: 0.75rem !important;
}

@media (max-width: 1023px) {
  .webphone-draggable {
    max-width: calc(100vw - 2rem);
  }

  .webphone-draggable :deep(.webphone) {
    width: calc(100vw - 2rem) !important;
    max-width: 360px !important;
  }
}
</style>
